/* eslint-disable no-console */
import {
	DescopeTheme,
	ParsedDescopeTheme,
	ParsedDescopeThemeVars
} from './theme';

// parses css string into a record of variables, values could be referenced using var(--variable-name)
export const parseCssVars = (cssString: string) => {
	const variables: Record<string, string> = {};

	const varNameRegex = /--([a-zA-Z0-9-]+)\s*:/g;
	let match = varNameRegex.exec(cssString);

	while (match !== null) {
		const varName = `--${match[1]}`;
		const valueStart = match.index + match[0].length;

		let depth = 0;
		let valueEnd = valueStart;

		for (let i = valueStart; i < cssString.length; i++) {
			const char = cssString[i];
			if (char === '(') {
				depth++;
			} else if (char === ')') {
				depth--;
			} else if ((char === ';' || char === '}') && depth === 0) {
				valueEnd = i;
				break;
			}
		}

		const value = cssString.substring(valueStart, valueEnd).trim();
		if (value) {
			variables[varName] = value;
		}

		match = varNameRegex.exec(cssString);
	}

	return variables;
};

// iteratively resolves variables that are referenced using var(--variable-name)
export const resolveCssVars = (vars: Record<string, string>) => {
	const resolvedVars = { ...vars };
	const maxIterations = 2;

	// eslint-disable-next-line security/detect-unsafe-regex
	const varRegex = /var\((--[a-zA-Z0-9-]+)\s*(?:,\s*(.*?))?\s*\)/g;

	for (let i = 0; i < maxIterations; i++) {
		let wasResolved = false; // Did we make any progress on this pass?
		let hasUnresolved = false; // Are there any 'var()' strings left?

		for (const key of Object.keys(resolvedVars)) {
			const value = resolvedVars[key];
			if (typeof value === 'string' && value.includes('var(')) {
				hasUnresolved = true;

				// eslint-disable-next-line @typescript-eslint/no-loop-func
				const newValue = value.replace(varRegex, (match, varName) => {
					// Get the value of the variable we're trying to replace
					const replacement = resolvedVars[varName];

					if (replacement !== undefined && replacement !== value) {
						// We found a value for it. Use it.
						wasResolved = true;
						return replacement;
					}

					// No value, no fallback. Can't resolve this yet.
					return match;
				});

				resolvedVars[key] = newValue;
			}
		}

		// If we went a whole pass and resolved nothing, we're stuck.
		if (!wasResolved && hasUnresolved) {
			console.warn('Theme parse: Could not resolve all variables.');
			return resolvedVars;
		}

		// If no 'var()' strings are left, we're done.
		if (!hasUnresolved) {
			return resolvedVars;
		}
	}

	/* istanbul ignore next  */
	console.warn('Max iterations reached. Possible circular dependency.');
	/* istanbul ignore next  */
	return resolvedVars;
};

const LOADER_VAR = '--descope-admin-portal-loader-url';

export const parseDescopeTheme = (theme: DescopeTheme): ParsedDescopeTheme => {
	// Admin Portal shell tokens and loader (`--descope-admin-portal-*`) live in the
	// style globals, so they land in the resolved globals map directly.
	const resolvedLightVars = resolveCssVars(parseCssVars(theme.light.globals));
	const resolvedDarkVars = resolveCssVars(parseCssVars(theme.dark.globals));

	let lightLogo;
	let lightFavicon;
	if (theme.light.components?.['descope-logo']?.host) {
		const lightLogoVars = parseCssVars(
			theme.light.components['descope-logo'].host
		);
		lightLogo = lightLogoVars['--descope-logo-url'];
		lightFavicon = lightLogoVars['--descope-favicon-url'];
	}
	let darkLogo;
	let darkFavicon;
	if (theme.dark.components?.['descope-logo']?.host) {
		const darkLogoVars = parseCssVars(
			theme.dark.components['descope-logo'].host
		);
		darkLogo = darkLogoVars['--descope-logo-url'];
		darkFavicon = darkLogoVars['--descope-favicon-url'];
	}

	return {
		light: {
			globalsVars: resolvedLightVars as ParsedDescopeThemeVars,
			logoUrl: lightLogo,
			faviconUrl: lightFavicon,
			loaderUrl: (resolvedLightVars as Record<string, string>)[LOADER_VAR]
		},
		dark: {
			globalsVars: resolvedDarkVars as ParsedDescopeThemeVars,
			logoUrl: darkLogo,
			faviconUrl: darkFavicon,
			loaderUrl: (resolvedDarkVars as Record<string, string>)[LOADER_VAR]
		}
	};
};
